import { type RefObject, useEffect } from "react";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * 열린 dialog 안으로 초점을 옮기고 Tab을 가두며, 닫히면 열기 전 요소로 초점을 돌려준다.
 * 스크롤 위치를 바꾸지 않도록 preventScroll로 초점을 옮긴다.
 */
export function useDialogFocusTrap(containerRef: RefObject<HTMLElement>, isActive: boolean) {
  useEffect(
    function trapFocusWhileOpen() {
      if (!isActive) return;
      const container = containerRef.current;
      const previouslyFocused = document.activeElement as HTMLElement | null;
      const firstFocusable = container?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      (firstFocusable ?? container)?.focus({ preventScroll: true });

      const trapTab = (event: KeyboardEvent) => {
        if (event.key !== "Tab" || !container) return;
        const focusables = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (!first || !last) {
          event.preventDefault();
          return;
        }
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      };

      document.addEventListener("keydown", trapTab);
      return () => {
        document.removeEventListener("keydown", trapTab);
        previouslyFocused?.focus?.({ preventScroll: true });
      };
    },
    [containerRef, isActive],
  );
}
